/**
 * Consent module for the frontend analytics pipeline.
 *
 * Card LeZvSY6e — OBS F0.4 Consent module.
 *
 * Contract
 * --------
 * - localStorage-backed, versioned via `CONSENT_VERSION`. When the version
 *   stored on disk does not match the constant, `getConsent()` returns
 *   `null` and the app must re-prompt the user (invalidation of a stale
 *   contract).
 * - `setConsent()` persists the value and dispatches a
 *   `CustomEvent('consent-changed')` on `window` so subscribers (e.g. the
 *   `useConsent` hook, analytics wiring) can react without polling.
 * - Pure module: no network calls, no React imports, no dependencies. Safe
 *   to import from anywhere (including SSR-guarded paths — every DOM
 *   access is wrapped in a `typeof window !== 'undefined'` check).
 *
 * This is F0.4 of the observability roll-out (batch 43). Wiring into
 * `main.tsx` / Sentry / analytics providers is intentionally out of scope
 * and comes in F1/F2/F3.
 */

/**
 * Bump this constant to invalidate every consent stored under the previous
 * contract. Any bump requires a change doc explaining WHY the previous
 * consent is no longer valid (new categories, wording change, etc.).
 */
export const CONSENT_VERSION = 1;

/**
 * Custom-event name dispatched on `window` when `setConsent()` or
 * `clearConsent()` runs. Consumers subscribe via `window.addEventListener`
 * to react without polling.
 */
export const CONSENT_CHANGED_EVENT = 'consent-changed';

/**
 * localStorage key. Includes the version suffix so a future major-version
 * bump (v2 = different key) can co-exist with v1 during a migration
 * window instead of overwriting it.
 */
const STORAGE_KEY = 'inbest.consent.v1';

/**
 * Value of a single consent category. Matches the GTM / GA4 "consent mode"
 * vocabulary so the wiring layer can pass it through unchanged.
 */
export type ConsentValue = 'granted' | 'denied';

/**
 * Full consent record persisted to localStorage.
 */
export interface Consent {
  analytics: ConsentValue;
  marketing: ConsentValue;
  /** Version of the consent contract this record was captured under. */
  version: number;
  /** ISO-8601 timestamp of when the user accepted / rejected. */
  updatedAt: string;
}

/**
 * Shape accepted by `setConsent()`. `version` and `updatedAt` are stamped
 * by the module itself so callers cannot forge either field.
 */
export type ConsentInput = Omit<Consent, 'version' | 'updatedAt'>;

/**
 * `true` when `localStorage` is reachable AND writable. Some browsers
 * throw on access when storage is disabled (private mode, quota exceeded,
 * disabled by policy) — we degrade gracefully to "no persistence".
 */
function isStorageAvailable(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const ls = window.localStorage;
    if (!ls) return false;
    // Probe write access — some Safari private-mode variants only fail on
    // `setItem`, not on read.
    const probeKey = '__inbest_consent_probe__';
    ls.setItem(probeKey, '1');
    ls.removeItem(probeKey);
    return true;
  } catch {
    return false;
  }
}

/**
 * Read the persisted consent. Returns `null` when:
 *   - localStorage is unreachable
 *   - no record has been persisted yet
 *   - the persisted record has an unknown `version` (stale contract)
 *   - the persisted record fails schema validation
 *
 * A `null` return means "the app must ask the user again", never "consent
 * is denied". Callers that need to gate a side effect on explicit consent
 * MUST use `hasAnalyticsConsent()` or check `consent?.analytics === 'granted'`
 * — treating `null` as denial is fine, treating it as grant is not.
 */
export function getConsent(): Consent | null {
  if (!isStorageAvailable()) return null;
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Corrupt payload — clear it so the app re-prompts cleanly rather
    // than repeatedly failing to parse the same string.
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* swallow */
    }
    return null;
  }

  if (!isValidConsent(parsed)) return null;
  if (parsed.version !== CONSENT_VERSION) return null;
  return parsed;
}

/**
 * Persist a new consent record. Stamps `version` (from `CONSENT_VERSION`)
 * and `updatedAt` (ISO now) so callers cannot forge either. Dispatches a
 * `CustomEvent('consent-changed')` on `window` with the full persisted
 * record as `detail` — the same shape `getConsent()` would return.
 *
 * No-ops when localStorage is unreachable EXCEPT for the event dispatch:
 * the event still fires so in-memory subscribers can react even in
 * private-mode browsers. This matches the "per-tab experience" the user
 * would otherwise expect.
 */
export function setConsent(input: ConsentInput): void {
  const record: Consent = {
    analytics: input.analytics,
    marketing: input.marketing,
    version: CONSENT_VERSION,
    updatedAt: new Date().toISOString(),
  };

  if (isStorageAvailable()) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch {
      // Quota exceeded or SecurityError — degrade gracefully. The event
      // below still fires so the current tab reacts.
    }
  }

  dispatchConsentChanged(record);
}

/**
 * Convenience helper for the common "should I fire analytics?" check.
 * Returns `true` only when the persisted record is present, version-current,
 * and `analytics === 'granted'`. Any other state returns `false` — this is
 * a hard privacy gate, not a UX toggle.
 */
export function hasAnalyticsConsent(): boolean {
  const consent = getConsent();
  return consent?.analytics === 'granted';
}

/**
 * Returns `true` when the app MUST re-prompt the user for a consent
 * decision. That covers three cases:
 *   - No consent has been captured yet (`localStorage` empty).
 *   - The persisted record is on a stale contract version.
 *   - The persisted record is corrupt (`getConsent()` returned `null`).
 *
 * Cookie-banner components use this as the sole visibility gate so
 * showing the banner is a pure function of persisted state — no
 * component-local booleans that can drift out of sync with storage.
 *
 * Card 71tCkSkN — OBS F0.5 CookieConsent banner + modal.
 */
export function needsConsentDecision(): boolean {
  return getConsent() === null;
}

/**
 * Remove the persisted consent record and notify subscribers with
 * `detail: null`. Used by cookie-banner "reset" affordances and by test
 * setup/teardown.
 */
export function clearConsent(): void {
  if (isStorageAvailable()) {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* swallow */
    }
  }
  dispatchConsentChanged(null);
}

// -------------------------- internals -------------------------------------

function dispatchConsentChanged(detail: Consent | null): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(
      new CustomEvent<Consent | null>(CONSENT_CHANGED_EVENT, { detail }),
    );
  } catch {
    /* swallow — event dispatch is best-effort */
  }
}

function isValidConsent(value: unknown): value is Consent {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    (v.analytics === 'granted' || v.analytics === 'denied') &&
    (v.marketing === 'granted' || v.marketing === 'denied') &&
    typeof v.version === 'number' &&
    typeof v.updatedAt === 'string'
  );
}
